import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Building, MagnifyingGlass } from "@phosphor-icons/react";
import { listClients } from "../lib/api";
import { ThemeToggle } from "../components/ThemeToggle";
import { useTranslation } from "react-i18next";

/**
 * v0.6.10 — Top-level History page.
 *
 * Was reachable ONLY via Admin → Clients (ADMIN-gated in the
 * frontend router) → per-row "history" button, even though the
 * underlying data (`vat_calculation_runs`, never deleted) and both
 * API endpoints (`GET /api/clients`, `GET /api/clients/{id}/history`)
 * have always been open to any authenticated role — the operator
 * role simply had no navigable path to it. This page gives history
 * its own stable, top-level URL (`/history`), open to ADMIN and
 * OPERATOR alike, so it no longer depends on admin-only client
 * management to reach. Picking a client here still lands on the
 * existing per-client detail page (`/clients/:id/history`), which
 * already renders every frozen filing for that entity — unchanged.
 */
export default function History() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    listClients()
      .then((rows) => { if (!cancelled) setClients(rows); })
      .catch((e) => { if (!cancelled) setError(e?.message || String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const q = query.trim().toLowerCase();
  const visible = q
    ? clients.filter((c) =>
        (c.code || "").toLowerCase().includes(q)
        || (c.name || "").toLowerCase().includes(q),
      )
    : clients;

  return (
    <div data-testid="history-page" className="min-h-screen bg-slate-50 dark:bg-slate-950 px-6 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{t("ui.history_page.eyebrow", { defaultValue: "Past filings" })}</div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50 flex items-center gap-2">
              <Clock size={22} weight="bold" className="text-teal-600 dark:text-teal-400" />
              {t("ui.history_page.title", { defaultValue: "History" })}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              data-testid="history-back"
              onClick={() => nav("/")}
              className="text-sm text-slate-700 dark:text-slate-200 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 dark:bg-slate-800 rounded-lg font-medium"
            >{t("ui.history_page.back", { defaultValue: "Back to dashboard" })}</button>
          </div>
        </div>

        <div className="text-sm text-slate-600 dark:text-slate-300">
          {t("ui.history_page.intro", { defaultValue: "Pick an entity to see every frozen filing on file for them — filing reference, period, who filed it, and the exported XLSX." })}
        </div>

        {loading && <div className="text-slate-600 dark:text-slate-300 text-sm">{t("ui.history_page.loading", { defaultValue: "Loading…" })}</div>}
        {error && (
          <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-900/40 border border-rose-200 dark:border-rose-800 rounded-lg px-4 py-3">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="relative">
              <MagnifyingGlass
                size={14}
                weight="bold"
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400 pointer-events-none"
              />
              <input
                type="search"
                data-testid="history-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("ui.admin_clients.search_placeholder", { defaultValue: "Search entities by code or name" })}
                className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-50 placeholder-slate-400 dark:placeholder-slate-500 focus:border-teal-500 dark:focus:border-teal-400 focus:ring-2 focus:ring-teal-500/30 outline-none"
              />
            </div>

            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
              {visible.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`history-client-${c.id}`}
                  onClick={() => nav(`/clients/${c.id}/history`)}
                  className="w-full flex items-center gap-3 px-5 py-4 border-b border-slate-100 dark:border-slate-800 last:border-b-0 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                >
                  <Building size={18} weight="regular" className="text-slate-500 dark:text-slate-400 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900 dark:text-slate-50 truncate">{c.name}</div>
                    <div className="text-xs tabular-nums text-slate-500 dark:text-slate-400">{c.code}</div>
                  </div>
                  <span className="text-xs font-medium text-teal-700 dark:text-teal-300">
                    {t("ui.history_page.view", { defaultValue: "View history →" })}
                  </span>
                </button>
              ))}
              {clients.length === 0 && (
                <div className="px-5 py-10 text-sm text-slate-500 dark:text-slate-400 text-center italic">
                  {t("ui.history_page.empty", { defaultValue: "No entities configured yet." })}
                </div>
              )}
              {clients.length > 0 && visible.length === 0 && (
                <div
                  data-testid="history-no-match"
                  className="px-5 py-10 text-sm text-slate-500 dark:text-slate-400 text-center"
                >
                  {t("ui.admin_clients.no_match", { q: query, defaultValue: `No entities match "${query}".` })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
